// src/components/UnifiedCalendar.tsx
import React, { useState, useEffect, useCallback } from 'react';
import { Card, Calendar, Badge, Typography, Tag, Space, Modal, Empty } from 'antd';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { diaryAPI, doctorsAPI } from '../utils/api';
import { useAuth } from '../App';

const { Text } = Typography;

interface DiaryEntry {
  type: 'glucose' | 'meal' | 'weight' | 'movement' | 'symptom' | 'abdomen' | 'visit' | 'lab_test';
  date: string;
  data: any;
}

interface CalendarData {
  [date: string]: DiaryEntry[];
}

interface UnifiedCalendarProps {
  selectedPatientId?: number | null;
}

export const UnifiedCalendar: React.FC<UnifiedCalendarProps> = ({ selectedPatientId = null }) => {
  const { user } = useAuth();
  const [selectedDate, setSelectedDate] = useState<Dayjs>(dayjs());
  const [calendarData, setCalendarData] = useState<CalendarData>({});
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedEntries, setSelectedEntries] = useState<DiaryEntry[]>([]);

  const loadCalendarData = useCallback(async () => {
    // Don't load if user is not available
    if (!user) {
      console.log('UnifiedCalendar: user not available yet, skipping load');
      return;
    }

    try {
      const today = dayjs();
      const startDate = today.subtract(3, 'month').format('YYYY-MM-DD');
      const endDate = today.add(1, 'month').format('YYYY-MM-DD');

      let glucose, meals, weight, movements, symptoms, abdomen, visits, labTests;

      // Load data based on user role and selected patient
      const userRole = user?.role;
      console.log('UnifiedCalendar: user =', user);
      console.log('UnifiedCalendar: user role =', userRole, 'selectedPatientId =', selectedPatientId);

      // Check if user is doctor (case-insensitive)
      const isDoctor = userRole === 'doctor' || userRole === 'Doctor';
      const isPatient = userRole === 'patient' || userRole === 'Patient';
      
      console.log('UnifiedCalendar: isDoctor =', isDoctor, 'isPatient =', isPatient);

      if (isDoctor) {
        if (selectedPatientId) {
          console.log('Loading patient data for doctor, patientId:', selectedPatientId);
          // Load patient data for doctor
          [glucose, meals, weight, movements, symptoms, abdomen, visits, labTests] = await Promise.all([
            doctorsAPI.getPatientGlucoseEntries(selectedPatientId, { from_date: startDate, to_date: endDate }).catch((e) => { console.error('Error loading glucose:', e); return { data: [] as any[] }; }),
            doctorsAPI.getPatientMealEntries(selectedPatientId, { from_date: startDate, to_date: endDate }).catch((e) => { console.error('Error loading meals:', e); return { data: [] as any[] }; }),
            doctorsAPI.getPatientWeightEntries(selectedPatientId, { from_date: startDate, to_date: endDate }).catch((e) => { console.error('Error loading weight:', e); return { data: [] as any[] }; }),
            doctorsAPI.getPatientMovementEntries(selectedPatientId, { from_date: startDate, to_date: endDate }).catch((e) => { console.error('Error loading movements:', e); return { data: [] as any[] }; }),
            doctorsAPI.getPatientSymptomEntries(selectedPatientId, { from_date: startDate, to_date: endDate }).catch((e) => { console.error('Error loading symptoms:', e); return { data: [] as any[] }; }),
            doctorsAPI.getPatientAbdomenEntries(selectedPatientId, { from_date: startDate, to_date: endDate }).catch((e) => { console.error('Error loading abdomen:', e); return { data: [] as any[] }; }),
            doctorsAPI.getPatientVisitEntries(selectedPatientId, { from_date: startDate, to_date: endDate }).catch((e) => { console.error('Error loading visits:', e); return { data: [] as any[] }; }),
            doctorsAPI.getPatientLabTestEntries(selectedPatientId, { from_date: startDate, to_date: endDate }).catch((e) => { console.error('Error loading lab tests:', e); return { data: [] as any[] }; }),
          ]);
        } else {
          console.log('No patient selected for doctor - returning empty data');
          // No patient selected - no data
          glucose = meals = weight = movements = symptoms = abdomen = visits = labTests = { data: [] };
        }
      } else if (isPatient) {
        console.log('Loading patient own data');
        // Load current patient's data
        [glucose, meals, weight, movements, symptoms, abdomen, visits, labTests] = await Promise.all([
          diaryAPI.getGlucoseEntries({ from_date: startDate, to_date: endDate }).catch(() => ({ data: [] as any[] })),
          diaryAPI.getMealEntries({ from_date: startDate, to_date: endDate }).catch(() => ({ data: [] as any[] })),
          diaryAPI.getWeightEntries({ from_date: startDate, to_date: endDate }).catch(() => ({ data: [] as any[] })),
          diaryAPI.getMovementEntries({ from_date: startDate, to_date: endDate }).catch(() => ({ data: [] as any[] })),
          diaryAPI.getSymptomEntries({ from_date: startDate, to_date: endDate }).catch(() => ({ data: [] as any[] })),
          diaryAPI.getAbdomenEntries({ from_date: startDate, to_date: endDate }).catch(() => ({ data: [] as any[] })),
          diaryAPI.getVisitEntries({ from_date: startDate, to_date: endDate }).catch(() => ({ data: [] as any[] })),
          diaryAPI.getLabTestEntries({ from_date: startDate, to_date: endDate }).catch(() => ({ data: [] as any[] })),
        ]);
      } else {
        // For doctors without selected patient, return empty data
        console.log('Unknown user role or no user - returning empty data. User:', user, 'Role:', userRole, 'isDoctor:', isDoctor, 'isPatient:', isPatient);
        // No data
        glucose = meals = weight = movements = symptoms = abdomen = visits = labTests = { data: [] };
      }

      const data: CalendarData = {};

      // Process glucose entries
      const glucoseData = (glucose.data || []) as any[];
      glucoseData.forEach((entry: any) => {
        const date = dayjs(entry.timestamp || entry.created_at).format('YYYY-MM-DD');
        if (!data[date]) data[date] = [];
        data[date].push({ type: 'glucose', date, data: entry });
      });

      // Process meal entries
      const mealsData = (meals.data || []) as any[];
      mealsData.forEach((entry: any) => {
        const date = dayjs(entry.timestamp).format('YYYY-MM-DD');
        if (!data[date]) data[date] = [];
        data[date].push({ type: 'meal', date, data: entry });
      });

      // Process weight entries
      const weightData = (weight.data || []) as any[];
      weightData.forEach((entry: any) => {
        const date = dayjs(entry.entry_date || entry.timestamp).format('YYYY-MM-DD');
        if (!data[date]) data[date] = [];
        data[date].push({ type: 'weight', date, data: entry });
      });

      // Process movement entries
      const movementsData = (movements.data || []) as any[];
      movementsData.forEach((entry: any) => {
        const date = dayjs(entry.entry_date || entry.movement_time).format('YYYY-MM-DD');
        if (!data[date]) data[date] = [];
        data[date].push({ type: 'movement', date, data: entry });
      });

      // Process symptom entries
      const symptomsData = (symptoms.data || []) as any[];
      symptomsData.forEach((entry: any) => {
        const date = dayjs(entry.entry_date || entry.timestamp).format('YYYY-MM-DD');
        if (!data[date]) data[date] = [];
        data[date].push({ type: 'symptom', date, data: entry });
      });

      // Process abdomen entries
      const abdomenData = (abdomen.data || []) as any[];
      abdomenData.forEach((entry: any) => {
        const date = dayjs(entry.entry_date || entry.timestamp).format('YYYY-MM-DD');
        if (!data[date]) data[date] = [];
        data[date].push({ type: 'abdomen', date, data: entry });
      });

      // Process visit entries
      const visitsData = (visits.data || []) as any[];
      visitsData.forEach((entry: any) => {
        const date = dayjs(entry.visit_date || entry.timestamp).format('YYYY-MM-DD');
        if (!data[date]) data[date] = [];
        data[date].push({ type: 'visit', date, data: entry });
      });

      // Process lab test entries
      const labTestsData = (labTests.data || []) as any[];
      labTestsData.forEach((entry: any) => {
        const date = dayjs(entry.test_date || entry.timestamp).format('YYYY-MM-DD');
        if (!data[date]) data[date] = [];
        data[date].push({ type: 'lab_test', date, data: entry });
      });

      setCalendarData(data);
    } catch (error) {
      console.error('Error loading calendar data:', error);
    } finally {
      // no-op
    }
  }, [selectedPatientId, user]);

  useEffect(() => {
    if (!user) {
      // Wait for user to load
      return;
    }
    loadCalendarData();
    // Обновляем календарь каждые 30 секунд для отображения новых записей
    const interval = setInterval(() => {
      loadCalendarData();
    }, 30000);
    return () => clearInterval(interval);
  }, [user, loadCalendarData]);

  const getEntryTypeColor = (type: string): string => {
    const colors: Record<string, string> = {
      glucose: '#7CB342',
      meal: '#42A5F5',
      weight: '#722ed1',
      movement: '#fa8c16',
      symptom: '#eb2f96',
      abdomen: '#13c2c2',
      visit: '#f5222d',
      lab_test: '#faad14',
    };
    return colors[type] || '#d9d9d9';
  };

  const getEntryTypeLabel = (type: string): string => {
    const labels: Record<string, string> = {
      glucose: 'Сахар',
      meal: 'Питание',
      weight: 'Вес',
      movement: 'Шевеления',
      symptom: 'Симптомы',
      abdomen: 'Живот',
      visit: 'Визит',
      lab_test: 'Анализы',
    };
    return labels[type] || type;
  };

  const dateCellRender = (value: Dayjs) => {
    const dateStr = value.format('YYYY-MM-DD');
    const entries = calendarData[dateStr] || [];
    const isToday = value.isSame(dayjs(), 'day');

    return (
      <div style={{ minHeight: '60px', padding: '4px', position: 'relative' }}>
        {isToday && (
          <div style={{ 
            position: 'absolute', 
            top: 0, 
            left: 0, 
            right: 0, 
            height: '3px', 
            backgroundColor: '#7CB342',
            borderRadius: '2px',
            zIndex: 1
          }} />
        )}
        <div style={{ marginTop: isToday ? '6px' : '0' }}>
          {entries.length > 0 ? (
            <>
              {entries.slice(0, 3).map((entry, idx) => (
                <Badge
                  key={idx}
                  color={getEntryTypeColor(entry.type)}
                  text={getEntryTypeLabel(entry.type)}
                  style={{ display: 'block', marginBottom: '2px', fontSize: '10px' }}
                />
              ))}
              {entries.length > 3 && (
                <Text type="secondary" style={{ fontSize: '10px' }}>
                  +{entries.length - 3} еще
                </Text>
              )}
            </>
          ) : isToday ? (
            <Text type="secondary" style={{ fontSize: '10px' }}>Сегодня</Text>
          ) : null}
        </div>
      </div>
    );
  };

  const handleDateSelect = (value: Dayjs) => {
    setSelectedDate(value);
    const dateStr = value.format('YYYY-MM-DD');
    const entries = calendarData[dateStr] || [];
    setSelectedEntries(entries);
    setModalVisible(true);
  };

  const renderEntryDetails = (entry: DiaryEntry) => {
    const { type, data } = entry;
    
    switch (type) {
      case 'glucose':
        return (
          <div>
            <Tag color="green">Сахар</Tag>
            <Text>Уровень: {data.glucose_level} ммоль/л</Text>
            <br />
            <Text type="secondary">{data.meal_type}, {data.measurement_time}</Text>
          </div>
        );
      case 'meal':
        return (
          <div>
            <Tag color="blue">Питание</Tag>
            <Text>Калории: {data.total_calories} ккал</Text>
            <br />
            <Text type="secondary">{data.meal_type}</Text>
          </div>
        );
      case 'weight':
        return (
          <div>
            <Tag color="purple">Вес</Tag>
            <Text>Вес: {data.weight_kg} кг</Text>
          </div>
        );
      case 'movement':
        return (
          <div>
            <Tag color="orange">Шевеление</Tag>
            <Text>Время: {dayjs(data.movement_time).format('HH:mm')}</Text>
          </div>
        );
      case 'symptom':
        return (
          <div>
            <Tag color="pink">Симптом</Tag>
            <Text>{data.symptom_text}</Text>
            <br />
            <Text type="secondary">Интенсивность: {'⭐'.repeat(data.intensity)}</Text>
          </div>
        );
      case 'abdomen':
        return (
          <div>
            <Tag color="cyan">Окружность живота</Tag>
            <Text>{data.abdomen_cm} см</Text>
            {data.photo_url && (
              <>
                <br />
                <img src={data.photo_url} alt="Фото" style={{ maxWidth: '200px', marginTop: '8px' }} />
              </>
            )}
          </div>
        );
      case 'visit':
        return (
          <div>
            <Tag color="red">Визит к врачу</Tag>
            <Text strong>{data.doctor_name}</Text>
            <br />
            <Text type="secondary">{data.notes}</Text>
          </div>
        );
      case 'lab_test':
        return (
          <div>
            <Tag color="gold">Анализ</Tag>
            <Text>{data.test_type || 'Лабораторный анализ'}</Text>
            {data.photo_url && (
              <>
                <br />
                <img src={data.photo_url} alt="Анализ" style={{ maxWidth: '200px', marginTop: '8px' }} />
              </>
            )}
          </div>
        );
      default:
        return <Text>{JSON.stringify(data)}</Text>;
    }
  };

  return (
    <>
      <Card style={{ borderRadius: '12px' }}>
        <Calendar
          dateCellRender={dateCellRender}
          onSelect={handleDateSelect}
          value={selectedDate}
          style={{ borderRadius: '8px' }}
        />
      </Card>

      <Modal
        title={`Записи за ${selectedDate.format('DD.MM.YYYY')}`}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={600}
      >
        {selectedEntries.length === 0 ? (
          <Empty description="Нет записей за этот день" />
        ) : (
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            {selectedEntries.map((entry, idx) => (
              <Card key={idx} size="small" style={{ borderRadius: '8px' }}>
                {renderEntryDetails(entry)}
              </Card>
            ))}
          </Space>
        )}
      </Modal>
    </>
  );
};
